"use client";

import { useState } from "react";
import Link from "next/link";
import { Mail, MapPin, Phone, Send, CheckCircle, TicketPlus, ArrowRight } from "lucide-react";
import { GoogleMap } from "@/components/GoogleMap";
import { ReviewPrompt } from "@/components/ReviewPrompt";
import { COMPANY } from "@/lib/facts";

const MAX_MESSAGE = 4000;

export default function KontaktPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [messageLen, setMessageLen] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const form = e.currentTarget;
    const consentEl = form.elements.namedItem("consent") as HTMLInputElement | null;
    const data = {
      name: (form.elements.namedItem("name") as HTMLInputElement).value,
      email: (form.elements.namedItem("email") as HTMLInputElement).value,
      phone: (form.elements.namedItem("phone") as HTMLInputElement).value,
      subject: (form.elements.namedItem("subject") as HTMLInputElement).value,
      message: (form.elements.namedItem("message") as HTMLTextAreaElement).value,
      website: (form.elements.namedItem("website") as HTMLInputElement).value,
      consent: consentEl?.checked === true,
    };

    try {
      const res = await fetch("/api/kontakt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || "Fehler beim Senden.");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold sm:text-5xl" style={{ color: "var(--text)" }}>
              Kontakt
            </h1>
            <p className="mt-4 text-lg" style={{ color: "var(--text-secondary)" }}>
              Haben Sie Fragen oder möchten ein Projekt besprechen? Schreiben Sie uns
              – wir melden uns schnellstmöglich.
            </p>
            <p className="mt-3 text-sm" style={{ color: "var(--text-tertiary)" }}>
              Bestandskunden finden unser{" "}
              <Link href="/dashboard/tickets/neu" className="font-medium text-primary hover:underline">
                Support-Ticket-System
              </Link>{" "}
              direkt im Kundenbereich – dort werden alle Anfragen strukturiert und
              nachverfolgbar bearbeitet.
            </p>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-5">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="space-y-6">
                  <div className="flex flex-col items-center rounded-xl border p-12 text-center" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}>
                    <CheckCircle className="h-16 w-16 text-success" />
                    <h2 className="mt-4 text-2xl font-bold" style={{ color: "var(--text)" }}>
                      Nachricht gesendet!
                    </h2>
                    <p className="mt-2 text-base" style={{ color: "var(--text-secondary)" }}>
                      Vielen Dank für Ihre Nachricht. Wir melden uns in der Regel innerhalb von
                      24 Stunden.
                    </p>
                  </div>
                  {/* Bewertungs-CTA: erscheint nur, wenn der Review-Link konfiguriert ist */}
                  <ReviewPrompt />
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-5 rounded-xl border p-8"
                  style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        maxLength={120}
                        autoComplete="name"
                        className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                        style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        maxLength={200}
                        autoComplete="email"
                        className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                        style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                      Telefon (optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      maxLength={50}
                      autoComplete="tel"
                      className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                      style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                      Betreff *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      maxLength={200}
                      className="w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                      style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
                    />
                  </div>

                  <div>
                    <div className="mb-1.5 flex items-baseline justify-between">
                      <label htmlFor="message" className="block text-sm font-medium" style={{ color: "var(--text)" }}>
                        Nachricht *
                      </label>
                      <span
                        className="text-xs"
                        style={{
                          color:
                            messageLen > MAX_MESSAGE * 0.9
                              ? "var(--text-warning-primary)"
                              : "var(--text-tertiary)",
                        }}
                      >
                        {messageLen} / {MAX_MESSAGE}
                      </span>
                    </div>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      minLength={10}
                      maxLength={MAX_MESSAGE}
                      onChange={(e) => setMessageLen(e.target.value.length)}
                      placeholder="Worum geht es? Je konkreter, desto besser können wir antworten."
                      className="w-full resize-none rounded-lg border px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                      style={{ backgroundColor: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
                    />
                    <p className="mt-1 text-[11px]" style={{ color: "var(--text-quaternary)" }}>
                      HTML/Code wird automatisch entfernt – plain text only.
                    </p>
                  </div>

                  {/* Honeypot */}
                  <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="consent"
                      required
                      className="mt-1 h-4 w-4 rounded accent-primary"
                    />
                    <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                      Ich habe die{" "}
                      <a href="/datenschutz" className="underline hover:text-primary">
                        Datenschutzerklärung
                      </a>{" "}
                      gelesen und stimme der Verarbeitung meiner Daten zu. *
                    </span>
                  </label>

                  {error && (
                    <p className="text-sm text-error font-medium" role="alert">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
                  >
                    <Send className="h-5 w-5" />
                    {loading ? "Wird gesendet..." : "Nachricht senden"}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-6 lg:col-span-2">
              <div className="rounded-xl border p-6" style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)" }}>
                <h2 className="text-lg font-bold" style={{ color: "var(--text)" }}>
                  Kontaktdaten
                </h2>
                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--text)" }}>Adresse</p>
                      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                        {COMPANY.legalName}<br />
                        {COMPANY.street}<br />
                        {COMPANY.zip} {COMPANY.city}, {COMPANY.region}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--text)" }}>Telefon</p>
                      <a href={`tel:${COMPANY.phoneTel}`} className="text-sm text-primary hover:underline">
                        {COMPANY.phoneInternational}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--text)" }}>E-Mail</p>
                      <a href={`mailto:${COMPANY.email}`} className="text-sm text-primary hover:underline">
                        {COMPANY.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Support-Ticket-System Hinweis */}
              <div
                className="rounded-xl border p-6"
                style={{
                  borderColor: "var(--border)",
                  backgroundColor: "var(--card-bg)",
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                    style={{
                      backgroundColor: "var(--fi-brand-bg)",
                      color: "var(--fi-brand-fg)",
                    }}
                  >
                    <TicketPlus className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h2
                      className="text-lg font-bold"
                      style={{ color: "var(--text)" }}
                    >
                      Sie sind bereits Kunde?
                    </h2>
                    <p
                      className="mt-1.5 text-sm"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      Für Anliegen rund um bestehende Projekte nutzen Sie bitte unser
                      Ticket-System. So bleiben alle Anfragen übersichtlich,
                      nachverfolgbar und werden schneller bearbeitet.
                    </p>
                    <Link
                      href="/dashboard/tickets"
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all hover:gap-2.5"
                    >
                      Zum Ticket-System
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Standort mit echter Google-Maps-Einbettung */}
              <div>
                <h2
                  className="text-lg font-bold"
                  style={{ color: "var(--text)" }}
                >
                  Standort
                </h2>
                <p
                  className="mt-1.5 text-sm"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  DMD Studio GmbH in Salz bei Bad Neustadt an der Saale.
                </p>
                <div className="mt-4">
                  <GoogleMap height={360} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
